import { type ReactElement, useState } from 'react'
import { isNotEmpty } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Text, TextInput } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { toShowingCountText, useAccountStatuses, useTranslation, useWorkflowForCanvassFeature } from '@front/model'
import { ButtonAdd, NotReady, Page, TDrawer } from '@components'
import { useDnkServerState } from './useDnkServerState'
import { DnkVirtualTable } from './DnkVirtualTable'
import { AddDoNotKnock } from './AddDoNotKnock'

function DoNotKnock(): ReactElement {
  const { isEnabled: isWorkflowEnabled, isLoading } = useWorkflowForCanvassFeature()
  if (isLoading) return <NotReady isLoading={isLoading} />
  if (!isWorkflowEnabled) return <LegacyDNK />
  return (
    <NotReady error='Do Not Knock is not available with workflows. Please use the accounts screen to look for restricted accounts instead.' />
  )
}

const LegacyDNK = (): ReactElement => {
  const { t } = useTranslation()
  const [opened, { toggle, close }] = useQueryDisclosure('add')
  const [query, setQuery] = useState('')
  const { data: accountStatuses } = useAccountStatuses()
  const restrictedStatuses = accountStatuses?.statuses.filter((status) => status.restricted === true) ?? []
  const statusIds = restrictedStatuses.map((status) => status.statusId)
  const serverState = useDnkServerState(query, statusIds)

  return (
    <Page>
      <Page.Heading title={t('Do Not Knock')}>{isNotEmpty(statusIds) && <ButtonAdd onClick={toggle} />}</Page.Heading>
      <Page.FunctionBar>
        <TextInput
          flex={1}
          placeholder={t('Search...')}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        {serverState.total && (
          <Text c='dimmed'>{toShowingCountText(serverState.data.length, serverState.total, 'record', 'records')}</Text>
        )}
      </Page.FunctionBar>

      <Page.Content>
        <DnkVirtualTable restrictedIds={statusIds} {...serverState} />
      </Page.Content>
      {isNotEmpty(restrictedStatuses) && (
        <TDrawer opened={opened} size='lg' title={t('Add Restricted Account')} onClose={close}>
          <AddDoNotKnock restrictedStatuses={restrictedStatuses} onAddSuccess={close} />
        </TDrawer>
      )}
    </Page>
  )
}

export const Route = createFileRoute('/dnk/')({
  component: DoNotKnock,
})
